import { SD_BASE } from "@sd/lib/sdPaths";

/**
 * Ticket queues visible to ServiceDesk_Agent in the sidebar.
 * Super Agent / Admin may use additional queues (all, unassigned, vip, …).
 */
export const AGENT_ALLOWED_TICKET_QUEUES = [
  "project-status",
  "report",
  "my-tickets",
  "kanban",
] as const;

export type AgentAllowedTicketQueue = (typeof AGENT_ALLOWED_TICKET_QUEUES)[number];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Path under `/sd` (leading slash, no `/sd` prefix). */
export function sdRelativePath(pathname: string): string {
  const raw = pathname.replace(/\/+$/, "") || "/";
  if (raw === SD_BASE || raw === `${SD_BASE}/`) return "/";
  if (raw.startsWith(`${SD_BASE}/`)) return raw.slice(SD_BASE.length) || "/";
  return raw;
}

export function isAgentAllowedTicketQueue(queue: string | undefined): boolean {
  if (!queue) return false;
  return (AGENT_ALLOWED_TICKET_QUEUES as readonly string[]).includes(queue);
}

/** Whether a logged-in Agent may open this `/sd/...` pathname. */
export function isAgentAllowedPath(pathname: string): boolean {
  const path = sdRelativePath(pathname);

  if (path === "/" || path === "") return true;

  if (/^\/tickets\/view\/[^/]+$/.test(path)) return true;
  if (/^\/ticket\/[^/]+$/.test(path)) return true;

  const ticketQueueMatch = path.match(/^\/tickets\/([^/]+)$/);
  if (ticketQueueMatch) {
    return isAgentAllowedTicketQueue(ticketQueueMatch[1]);
  }

  if (path === "/tickets") return true;
  if (path === "/projects") return true;

  const rootSegment = path.replace(/^\//, "").split("/")[0] ?? "";
  if (UUID_RE.test(rootSegment) && path === `/${rootSegment}`) {
    return true;
  }

  return false;
}
